import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { CustomerdatabaseService } from '../../services/customerdatabase.service';

export type TourPlacement = 'top' | 'bottom' | 'left' | 'right' | 'auto' | 'center';

export interface PlatformTourStep {
  id: string;
  kind?: 'tip' | 'prompts' | 'cta';
  heroIcon?: string;
  route?: string;
  selector?: string | string[];
  titleDe: string;
  titleEn: string;
  bodyDe: string;
  bodyEn: string;
  placement?: TourPlacement;
  spotlightPadding?: number;
  ctaLabelDe?: string;
  ctaLabelEn?: string;
  ctaRoute?: string;
  ctaAction?: string;
  ctaActionLabelDe?: string;
  ctaActionLabelEn?: string;
  ctaUrl?: string;
  ctaUrlLabelDe?: string;
  ctaUrlLabelEn?: string;
  showSkip?: boolean;
  waitForSelectorMs?: number;
  scrollIntoView?: boolean;
  prompts?: string[];
  requireConditions?: {
    interests?: string[];
    platform?: string;
  };
}

export interface PlatformTourState {
  steps: PlatformTourStep[];
  currentIndex: number;
  language: 'DE' | 'EN';
  active: boolean;
  isFirstRun: boolean;
}

const FIRESTORE_COMPLETION_FIELD = 'platformTourCompleted';
const FIRESTORE_PROGRESS_FIELD = 'platformTourCurrentStep';
const FIRESTORE_IN_PROGRESS_FIELD = 'platformTourInProgress';
const LOCALSTORAGE_KEY = 'platformTourCompletedAt';
const LOCALSTORAGE_PROGRESS_KEY = 'platformTourCurrentStep';

@Injectable({ providedIn: 'root' })
export class PlatformTourService {
  private state$ = new BehaviorSubject<PlatformTourState | null>(null);
  private ctaAction$ = new Subject<string>();
  private navGeneration = 0;

  constructor(
    private router: Router,
    private zone: NgZone,
    private customerDB: CustomerdatabaseService
  ) {}

  get state(): Observable<PlatformTourState | null> {
    return this.state$.asObservable();
  }

  get currentState(): PlatformTourState | null {
    return this.state$.value;
  }

  get ctaActions(): Observable<string> {
    return this.ctaAction$.asObservable();
  }

  emitCtaAction(action: string): void {
    if (action) this.ctaAction$.next(action);
  }

  isCompleted(): boolean {
    if (localStorage.getItem(LOCALSTORAGE_KEY)) return true;
    return false;
  }

  async hasEverCompletedInFirestore(): Promise<boolean> {
    try {
      const profileRef = this.customerDB.getProfile();
      if (!profileRef) return false;
      const snap = await profileRef.get();
      if (!snap || snap.empty) return false;
      const data: any = snap.docs[0].data() || {};
      return !!data[FIRESTORE_COMPLETION_FIELD];
    } catch {
      return false;
    }
  }

  async hasCompletedInFirestore(): Promise<boolean> {
    try {
      const profileRef = this.customerDB.getProfile();
      if (!profileRef) return false;
      const snap = await profileRef.get();
      if (!snap || snap.empty) return false;
      const data: any = snap.docs[0].data() || {};
      const done = !!data[FIRESTORE_COMPLETION_FIELD];
      if (done) localStorage.setItem(LOCALSTORAGE_KEY, Date.now().toString());
      return done;
    } catch {
      return false;
    }
  }

  async start(steps: PlatformTourStep[], language: 'DE' | 'EN' = 'DE', fromIndex: number = 0, isFirstRun: boolean = true): Promise<void> {
    if (!steps || steps.length === 0) return;
    const startIdx = Math.max(0, Math.min(fromIndex, steps.length - 1));
    const gen = ++this.navGeneration;
    this.state$.next({ steps, currentIndex: startIdx, language, active: true, isFirstRun });
    this.persistProgress(startIdx);
    await this.gotoStep(startIdx, gen);
  }

  async next(): Promise<void> {
    const s = this.state$.value;
    if (!s) return;
    if (s.currentIndex >= s.steps.length - 1) {
      await this.finish();
      return;
    }
    const nextIndex = s.currentIndex + 1;
    const gen = ++this.navGeneration;
    this.state$.next({ ...s, currentIndex: nextIndex });
    this.persistProgress(nextIndex);
    await this.gotoStep(nextIndex, gen);
  }

  async prev(): Promise<void> {
    const s = this.state$.value;
    if (!s) return;
    if (s.currentIndex <= 0) return;
    const prevIndex = s.currentIndex - 1;
    const gen = ++this.navGeneration;
    this.state$.next({ ...s, currentIndex: prevIndex });
    this.persistProgress(prevIndex);
    await this.gotoStep(prevIndex, gen);
  }

  async skip(): Promise<void> {
    await this.finish(true);
  }

  pause(): void {
    const s = this.state$.value;
    if (!s) return;
    this.navGeneration++;
    this.persistProgress(s.currentIndex);
    this.state$.next(null);
  }

  async getSavedProgressFromFirestore(): Promise<number | null> {
    try {
      const profileRef = this.customerDB.getProfile();
      if (!profileRef) return null;
      const snap = await profileRef.get();
      if (!snap || snap.empty) return null;
      const data: any = snap.docs[0].data() || {};
      if (data[FIRESTORE_COMPLETION_FIELD]) return null;
      if (!data[FIRESTORE_IN_PROGRESS_FIELD]) return null;
      const idx = data[FIRESTORE_PROGRESS_FIELD];
      return typeof idx === 'number' && idx > 0 ? idx : null;
    } catch {
      return null;
    }
  }

  async finish(skipped: boolean = false): Promise<void> {
    this.navGeneration++;
    this.state$.next(null);
    localStorage.setItem(LOCALSTORAGE_KEY, Date.now().toString());
    localStorage.removeItem(LOCALSTORAGE_PROGRESS_KEY);
    try {
      const profileRef = this.customerDB.getProfile();
      if (!profileRef) return;
      const snap = await profileRef.get();
      if (!snap || snap.empty) return;
      const docId = snap.docs[0].id;
      const update: any = {};
      update[FIRESTORE_COMPLETION_FIELD] = true;
      update['platformTourCompletedAt'] = Date.now();
      update[FIRESTORE_IN_PROGRESS_FIELD] = false;
      if (skipped) update['platformTourSkipped'] = true;
      await profileRef.doc(docId).update(update);
    } catch (e) {
      console.warn('[PlatformTour] failed to persist completion', e);
    }
  }

  private async persistProgress(currentIndex: number): Promise<void> {
    try {
      localStorage.setItem(LOCALSTORAGE_PROGRESS_KEY, String(currentIndex));
      const profileRef = this.customerDB.getProfile();
      if (!profileRef) return;
      const snap = await profileRef.get();
      if (!snap || snap.empty) return;
      const docId = snap.docs[0].id;
      await profileRef.doc(docId).update({
        [FIRESTORE_PROGRESS_FIELD]: currentIndex,
        [FIRESTORE_IN_PROGRESS_FIELD]: true,
        platformTourLastSeenAt: Date.now()
      });
    } catch (e) {
      console.warn('[PlatformTour] failed to persist progress', e);
    }
  }

  async runCtaRoute(route: string): Promise<void> {
    await this.router.navigateByUrl(route);
  }

  private async gotoStep(index: number, gen: number): Promise<void> {
    const s = this.state$.value;
    if (!s) return;
    const step = s.steps[index];
    if (!step) return;

    if (step.route && !this.routeMatches(step.route)) {
      try { await this.router.navigateByUrl(step.route); } catch {}
      if (gen !== this.navGeneration) return;
      await this.waitFrames(3);
      if (gen !== this.navGeneration) return;
    }

    if (step.selector) {
      const timeout = step.waitForSelectorMs ?? 3000;
      const el = await this.waitForElement(step.selector, timeout, gen);
      if (gen !== this.navGeneration) return;
      if (el && step.scrollIntoView !== false) {
        try { el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' }); } catch {}
        await this.waitFrames(8);
        if (gen !== this.navGeneration) return;
      }
    }

    const latest = this.state$.value;
    if (!latest || latest.currentIndex !== index) return;
    this.state$.next({ ...latest });
  }

  private routeMatches(route: string): boolean {
    const currentUrl = (this.router.url || '').split('?')[0];
    return currentUrl === route || currentUrl.startsWith(route);
  }

  private async waitForElement(selector: string | string[], timeoutMs: number, gen?: number): Promise<HTMLElement | null> {
    const selectors = Array.isArray(selector) ? selector : [selector];
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      if (gen !== undefined && gen !== this.navGeneration) return null;
      for (const sel of selectors) {
        const el = document.querySelector(sel) as HTMLElement | null;
        if (el && el.getClientRects().length > 0) return el;
      }
      await new Promise(r => setTimeout(r, 80));
    }
    return null;
  }

  private waitFrames(frames: number): Promise<void> {
    return new Promise(resolve => {
      let count = 0;
      const tick = () => {
        count++;
        if (count >= frames) resolve();
        else requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });
  }
}
